export const TWIN_SEQUENCE = {
	ingestSeconds: 10,
	cycleSeconds: 24,
	totalSeconds: 34,
	reducedMotionSeconds: 23,
} as const;

const EPOCH_KEY = "__mlxTwinSequenceEpoch";

declare global {
	interface Window {
		__mlxTwinSequenceEpoch?: number;
	}
}

export function normaliseTwinSequenceTime(nowMs: number, epochMs: number) {
	const elapsedSeconds = Math.max(0, nowMs - epochMs) / 1000;
	return elapsedSeconds % TWIN_SEQUENCE.totalSeconds;
}

export function getTwinSequenceTime(nowMs = performance.now()) {
	window[EPOCH_KEY] ??= nowMs;
	return normaliseTwinSequenceTime(nowMs, window[EPOCH_KEY]);
}

/**
 * The time a stage's instrument, arrival layer and riser all read, in
 * sequence seconds: the site's shared looping clock, or one a stage owns.
 */
export type TwinSequenceClock = {
	read: (nowMs?: number) => number;
	/** Sequence seconds per second of real time. */
	rate: number;
	/** Whether the sequence restarts at its end; a clock that does not holds its last second. */
	loops: boolean;
	/** Whether the sequence is under way; a stage's own clock waits for its instrument. */
	running: boolean;
};

/** Every looping instrument on a page shares one epoch, so they breathe in phase. */
export const SHARED_TWIN_CLOCK: TwinSequenceClock = {
	read: getTwinSequenceTime,
	rate: 1,
	loops: true,
	running: true,
};

/** The settled rings, where a sequence that plays once comes to rest. */
export const TWIN_SETTLED_SECONDS = TWIN_SEQUENCE.ingestSeconds + 11;

/**
 * A clock that plays the sequence once from `startMs`, `rate` times its
 * written pace, and rests on the settled rings. It begins at `fromSeconds`
 * of the sequence and reads that until it has a start: zero, the empty
 * glass, for a stage that plays the whole sequence; a later second for one
 * that joins it part-way (the swarm already in the glass, about to sort).
 */
export function runOnceTwinClock(rate: number, startMs: number | null, fromSeconds = 0): TwinSequenceClock {
	return {
		rate,
		loops: false,
		running: startMs !== null,
		read: (nowMs = performance.now()) =>
			startMs === null ? fromSeconds : Math.min(TWIN_SETTLED_SECONDS, fromSeconds + (Math.max(0, nowMs - startMs) / 1000) * rate),
	};
}
